import { useMemo } from "react";

import { useAllIssues } from "v2/hooks/issues/useIssues";
import { useCounters } from "v2/hooks/billing/useCounters";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";

import { IssueSeverity } from "providers/IssuesProvider";

import { Banner } from "./Banner";
import { CountersData } from "pages/dashboard/PlanOverviewAndStatsCard";

import { PricingOptions } from "types/billing/pricing-options/PricingOptions";


function joinFeatures(features: string[]) {
  if (features.length < 1) {
    return "";
  }

  if (features.length < 2) {
    return features[0];
  }

  return features.slice(0, -1).join(", ") + " or " + features.slice(-1);
}

// TODO should move this to the backend so it can be tested properly
const hasOutOfCreditsIssue = (counters: CountersData | undefined, pricingOptions: PricingOptions | null | undefined) => {
  if (counters === undefined) {
    return undefined;
  }

  if (!(counters.totalCredits)) {
    return undefined;
  }

  // don't show anything if any the new pricing options is already enabled
  if (!pricingOptions || pricingOptions?.["auto-upgrade"]?.enabled || pricingOptions?.["pay-as-you-go"]?.enabled) {
    return undefined;
  }

  const ratio = counters.requestCount / counters.totalCredits;

  // Can't dismiss these issues
  if (counters.requestCount < counters.totalCredits && ratio >= 0.8) {
    const features = pricingOptions.renewal.allowed ? [ "renewing early", "enabling auto-renewal" ] : [];
    if (pricingOptions["auto-upgrade"].allowed) {
      features.unshift("activating auto-upgrade for your plan");
      features.push("upgrading your plan");
    } else if (pricingOptions["pay-as-you-go"].allowed) {
      features.unshift("activating pay-as-you-go for your plan");
    }

    if (features.length === 0) {
      features.push("upgrading your plan");
    }

    return {
      title: "You are running low on credits. Consider " + joinFeatures(features) + " to avoid interruption. 👉 Go to [billing page|billing_page]",
      severity: 'info' as IssueSeverity,
      dismissible: false
    };
  } else if (counters.requestCount >= counters.totalCredits) {
    const features = pricingOptions.renewal.allowed ? [ "renew", "enable auto-renewal" ] : [];
    if (pricingOptions["auto-upgrade"].allowed) {
      features.unshift("enable auto-upgrade");
      features.push("upgrade your plan");
    } else if (pricingOptions["pay-as-you-go"].allowed) {
      features.unshift("enable pay-as-you-go");
    }

    if (features.length === 0) {
      features.push("upgrade your plan");
    }

    return {
      title: "‼️ You've exhausted your credits. Please " + joinFeatures(features) + " on the [billing page|billing_page] to resume scraping",
      severity: 'warning' as IssueSeverity,
      dismissible: false
    };
  }
  return undefined;
}

export default function IssuesBanner() {

  const { issues } = useAllIssues();
  const { counters } = useCounters();
  const { pricingOptions } = usePricingOptions();

  const primaryIssue = useMemo(() => issues && issues[0], [ issues ]);
  const outOfCreditsIssue = hasOutOfCreditsIssue(counters, pricingOptions);

  return (
    <>
      { primaryIssue && (
        <Banner title={primaryIssue.title} dismissible={primaryIssue.dismissible} severity={primaryIssue.severity} url={primaryIssue.issueStatusUrl}/>
      )}
      { outOfCreditsIssue && (
        <Banner title={outOfCreditsIssue.title} dismissible={outOfCreditsIssue.dismissible} severity={outOfCreditsIssue.severity}/>
      )}
    </>
  );

};
